import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import * as api from "../../api";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {useNavigate} from "react-router-dom";


export const useIncidentsQuery = (incidentsFilters={}) => {
    const query = useQuery({
        queryKey: ['incidents', incidentsFilters],
        queryFn: () => api.Incidents.getIncidents(incidentsFilters),
        keepPreviousData: true
    });

    // Prefetch the next page!
    const queryClient = useQueryClient()
    const {isPreviousData} = query;
    const hasMoreData = query.data?.page_count > incidentsFilters.pageIndex + 1;

    // note that models23Filters should be memorized to avoid useless prefetch
    useEffect(() => {
        if (!isPreviousData && hasMoreData) {
            const nextPageFilter = {...incidentsFilters, pageIndex: incidentsFilters.pageIndex + 1}
            queryClient.prefetchQuery({
                queryKey: ['incidents', nextPageFilter],
                queryFn: () => api.Incidents.getIncidents(nextPageFilter),
            })
        }
    }, [isPreviousData, hasMoreData, incidentsFilters, queryClient])

    return query;
}


export const useIncidentQuery = (incidentId) => {
    return useQuery({
        queryKey: ['incident', incidentId],
        queryFn: () => api.Incidents.getIncident(incidentId),
        keepPreviousData: true,
    });
}

export const useIncidentPre2021Query = (incidentId) => {
    return useQuery({
        queryKey: ['incident', incidentId],
        queryFn: () => api.Incidents.getIncidentPre2021(incidentId),
        keepPreviousData: true,
    });

}

export const useIncidentRegistryQuery = () => {
    const {i18n} = useTranslation();
    const locale = i18n.language;

    return useQuery({
        queryKey: ['registry', 'incident', locale],
        queryFn: () => api.Registry.getIncidentRegistry(locale),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    });
}

export const useIncidentSettingsQuery = () => {
    return useQuery({
        queryKey: ['settings', 'incident'],
        queryFn: () => api.Settings.getIncidentSettings(),
        keepPreviousData: true,
        staleTime: 1000 * 60 * 60 * 24 * 7, // 1 week
    });
}


export const useCreateIncident = ({disableNotification}) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Incidents.createIncident(data),
        onSuccess: () => {
            queryClient.invalidateQueries(['incidents']);
            queryClient.invalidateQueries(['geo', 'incidents']);
            if (!disableNotification) {
                showSuccessNotification(t('query.createSuccess'));
            }
        }
    });
}


export const useUpdateIncident = (incidentId, {disableNotification}) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Incidents.updateIncident(incidentId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['incidents']);
            queryClient.invalidateQueries(['geo', 'incidents']);
            if(!disableNotification) {
                showSuccessNotification(t('query.updateSuccess'));
            }
        }
    });
}

export const useUpdateIncidentPre2021 = (incidentId) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.Incidents.updateIncidentPre2021(incidentId, data),
        onSuccess: () => {
            queryClient.invalidateQueries(['incidents']);
            queryClient.invalidateQueries(['geo', 'incidents']);
            showSuccessNotification(t('query.updateSuccess'));
        }
    });
}

export const useIncidentDeleteMutation = () => {
    const {t} = useTranslation(["incidents"])
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (id) => await api.Incidents.deleteIncident(id),
        onSuccess: async () => {
            await navigate('/incidents');
            showSuccessNotification(t('query.deleteSuccess'));
            queryClient.invalidateQueries(['incidents']);
            queryClient.invalidateQueries(['geo', 'incidents']);
        }
    });
}

export const useIncidentPre2021DeleteMutation = () => {
    const {t} = useTranslation(["incidents"])
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async (id) => await api.Incidents.deleteIncidentPre2021(id),
        onSuccess: async () => {
            await navigate('/incidents');
            showSuccessNotification(t('query.deleteSuccess'));
            queryClient.invalidateQueries(['incidents']);
            queryClient.invalidateQueries(['geo', 'incidents']);
        }
    });
}


export const useCreateIncidentAttachment = ({disableNotification}) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async ({id, data}) => await api.IncidentAttachment.createIncidentAttachment(id, data),
        onSuccess: (res) => {
            queryClient.invalidateQueries(['incidents', res.incidentId]);
            if(!disableNotification) {
                showSuccessNotification(t('query.createAttachmentSuccess'));
            }

        }
    })
}


export const useUpdateIncidentAttachment = ({disableNotification}) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async ({id, attachmentId, data}) => await api.IncidentAttachment.updateIncidentAttachment(id, attachmentId, data),
        onSuccess: (res) => {
            queryClient.invalidateQueries(['incidents', res.incidentId]);
            if(!disableNotification){
                showSuccessNotification(t('query.updateAttachmentSuccess'));
            }
        }
    })
}


export const useDeleteIncidentAttachment = ({disableNotification}) => {
    const {t} = useTranslation(["incidents"])
    const queryClient = useQueryClient();
    const {showSuccessNotification}  = useFeedbackTools();

    return useMutation({
        mutationFn: async ({id, attachmentId}) => await api.IncidentAttachment.deleteIncidentAttachment(id, attachmentId),
        onSuccess: (res) => {
            queryClient.invalidateQueries(['incidents', res.incidentId]);
            if(!disableNotification){
                showSuccessNotification(t('query.deleteAttachmentSuccess'));
            }
        }
    })
}


export const useIncidentExportMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Incidents.exportIncidents(data),
        onSuccess: (data, ) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}


export const usePeopleCaughtExportMutation = () => {
    return useMutation({
        mutationFn: async (data) => await api.Incidents.exportPeopleCaught(data),
        onSuccess: (data, ) => {
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(new Blob([data.data]));
            link.download = data.fileName; // specify the filename
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    });
}

export const useCreateIncidentExportTask = () => {
    return useMutation({
        mutationFn: async ({workgroupId, filters, data}) => await api.ExportTask.createIncidentExportTask(workgroupId, filters, data),
    });
}

export const useCreatePeopleCaughtExportTask = () => {
    return useMutation({
        mutationFn: async ({workgroupId, filters, data}) => await api.ExportTask.createPeopleCaughtExportTask(workgroupId, filters, data),
    });
}


export const useIncidentTotalsQuery = (incidentsFilters={}) => {
    return useQuery({
        queryKey: ['incidentTotals', incidentsFilters],
        queryFn: () => api.Incidents.getIncidentTotals(incidentsFilters),
        keepPreviousData: true,
    });
}